import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  AccessControl,
  Button,
  CircularProgressLoader,
  FieldFileInput,
  FieldSelect,
  FieldTextInput,
  PERMISSION_KEYS,
  PERMISSION_TYPES,
  ToastBar,
  ValidationError,
} from '../../components';
import { DomesManagementService, SpecialPostsService } from '../../services';
import type { SpecialPostStatus, SpecialPostType } from '../../types/specialPosts';
import StatusChip from '../EnterVice/parts/StatusChip';
import { describeError, UiError } from '../EnterVice/parts/errors';
import ErrorBox from '../EnterVice/parts/ErrorBox';
import '../LayoutWrapperMain/LayoutWrapperMain.module.scss';
import '../EnterVice/entervice.scss';

interface FormValues {
  name: string;
  key: string;
  status: SpecialPostStatus;
  domeName: string;
  domeDescription: string;
  // a FileList while a new icon is picked, the stored URL otherwise
  icon: any;
}

const KEY_REGEX = /^[a-z0-9_]{2,50}$/;
const ICON_MAX_BYTES = 50 * 1024 * 1024;
const ICON_TYPES = ['image/jpg', 'image/jpeg', 'image/png'];
const STATUS_OPTIONS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const DEFAULTS: FormValues = {
  name: '', key: '', status: 'active', domeName: '', domeDescription: '', icon: '',
};

const isNewFile = (value: any) => Boolean(value && typeof value !== 'string' && value.length);

const slugify = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 50);

const schema = yup.object().shape({
  name: yup.string().trim().required('Name is required.').min(3, 'Name needs at least 3 characters.').max(100, 'Name can have at most 100 characters.'),
  key: yup.string().required('Key is required.').matches(KEY_REGEX, 'Key: 2-50 lowercase letters, digits or _.'),
  domeName: yup.string().trim().required('Dome name is required.').min(3, 'Dome name needs at least 3 characters.').max(100, 'Dome name can have at most 100 characters.'),
  domeDescription: yup.string().max(250, 'Dome description can have at most 250 characters.'),
  icon: yup.mixed()
    .test('required', 'Dome icon is required.', (value) => (typeof value === 'string' ? value.length > 0 : isNewFile(value)))
    .test('fileSize', 'Dome icon must be 50 MB or smaller.', (value) => !isNewFile(value) || value[0].size <= ICON_MAX_BYTES)
    .test('fileFormat', 'Dome icon must be a JPG or PNG file.', (value) => !isNewFile(value) || ICON_TYPES.includes(value[0].type)),
});

const toFormValues = (item: SpecialPostType): FormValues => ({
  name: item.name,
  key: item.key,
  status: item.status,
  domeName: item.dome ? item.dome.name : '',
  domeDescription: (item.dome && item.dome.description) || '',
  icon: (item.dome && item.dome.selectedImage) || '',
});

/** `/specialposts/addSpecialPost` and `/specialposts/editSpecialPost/:id` (SCRUM-247). */
const ManageSpecialPost: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState<SpecialPostType | null>(null);
  const [ready, setReady] = useState(!isEdit);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<UiError | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<UiError | null>(null);
  const [toast, setToast] = useState('');
  const keyTouched = useRef(false);

  const formProps = useForm<FormValues>({ resolver: yupResolver(schema), defaultValues: DEFAULTS });
  const {
    handleSubmit, reset, setValue, watch, formState: { errors },
  } = formProps;
  const nameValue = watch('name');

  // toast handed over by the create → edit redirect
  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (state && state.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!isEdit || !id) return undefined;
    let cancelled = false;
    setReady(false);
    setLoadError('');
    SpecialPostsService.get(id)
      .then((loaded) => {
        if (cancelled) return;
        setItem(loaded);
        reset(toFormValues(loaded));
        keyTouched.current = true;
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(describeError(e, 'Failed to load the special post type.').message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, reset]);

  // the key follows the name until the admin edits it by hand (create only; the key never changes later)
  useEffect(() => {
    if (!isEdit && !keyTouched.current) setValue('key', slugify(nameValue || ''));
  }, [nameValue, isEdit, setValue]);

  const uploadIcon = async (icon: any): Promise<string> => {
    if (!isNewFile(icon)) return icon;
    const form = new FormData();
    form.append('file', icon[0]);
    const res: any = await DomesManagementService.uploadpicture(form);
    const url = res && res.data && res.data.data && res.data.data.url;
    if (!url) throw new Error('The icon could not be uploaded. Please try again.');
    return url;
  };

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    setFormError(null);
    try {
      const iconUrl = await uploadIcon(values.icon);
      const dome = {
        name: values.domeName.trim(),
        description: (values.domeDescription || '').trim(),
        selectedImage: iconUrl,
        nonSelectedImage: iconUrl,
      };
      if (isEdit && id) {
        const updated = await SpecialPostsService.update(id, { name: values.name.trim(), dome });
        setItem(updated);
        reset(toFormValues(updated));
        setToast('Special post type saved.');
      } else {
        const created = await SpecialPostsService.create({
          key: values.key, name: values.name.trim(), status: values.status, dome,
        });
        navigate(`/specialposts/editSpecialPost/${created._id}`, {
          replace: true,
          state: { toast: `“${created.name}” created with its dome “${created.dome.name}”.` },
        });
      }
    } catch (e) {
      setFormError(describeError(e, 'Could not save the special post type.'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = (status: SpecialPostStatus) => {
    if (!id) return;
    setStatusBusy(true);
    setStatusError(null);
    SpecialPostsService.setStatus(id, status)
      .then((updated) => {
        setItem(updated);
        setToast(status === 'active' ? 'Activated. The dome is live in the apps.' : 'Deactivated. The dome is hidden in the apps.');
      })
      .catch((e) => setStatusError(describeError(e, 'Could not change the status.')))
      .finally(() => setStatusBusy(false));
  };

  const firstFormError = Object.keys(errors).length > 0
    ? ((Object.values(errors)[0] as any)?.message?.toString() as string | undefined)
    : undefined;

  if (loadError) {
    return (
      <div className="cardBg">
        <ValidationError error={loadError} />
        <div className="buttonWrapper">
          <Button buttonType="button" onClick={() => navigate('/specialposts')}>Back to special posts</Button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return <div className="savingLoader"><CircularProgressLoader /></div>;
  }

  return (
    <>
      {toast && <ToastBar message={toast} handleCloseMethod={() => setToast('')} />}
      <div className="ev-editor">
        <div className="cardBg addDetailsPage">
          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <h2 className="sign_text">{isEdit ? 'Edit Special Post Type' : 'New Special Post Type'}</h2>

            {item && (
              <div className="ev-section">
                <div className="ev-status-row">
                  <StatusChip status={item.status} />
                  <span>Dome <strong>{item.dome ? item.dome.name : '—'}</strong></span>
                  <span>Dome rank <strong>{item.dome && item.dome.rank ? item.dome.rank : '—'}</strong></span>
                </div>
                <ErrorBox error={statusError} />
                <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={PERMISSION_TYPES.EDIT}>
                  <div className="ev-inline-actions">
                    {item.status === 'active' ? (
                      <Button buttonType="button" className="ev-btn-ghost" onClick={() => changeStatus('inactive')} disabled={statusBusy}>
                        {statusBusy ? 'Working…' : 'Deactivate'}
                      </Button>
                    ) : (
                      <Button buttonType="button" onClick={() => changeStatus('active')} disabled={statusBusy}>
                        {statusBusy ? 'Working…' : 'Activate'}
                      </Button>
                    )}
                  </div>
                </AccessControl>
              </div>
            )}

            <div className="ev-section">
              <h3>Special post type</h3>
              <p className="ev-hint">
                The key must match the key the app’s creation flow uses (ENTER VICE: enter_vice). Posts carry it, so it cannot be changed after creation.
              </p>
              <div className="ev-grid2">
                <FieldTextInput id="name" name="name" formName="specialPost" className="textInput" type="text" formProps={formProps} label="*Name" disabled={saving} />
                <FieldTextInput
                  id="key"
                  name="key"
                  formName="specialPost"
                  className="textInput"
                  type="text"
                  formProps={formProps}
                  label="*Key (a-z, 0-9, _)"
                  disabled={saving || isEdit}
                  handleMainInputChange={() => { keyTouched.current = true; return null; }}
                />
              </div>
              {!isEdit && (
                <FieldSelect id="status" name="status" label="Status" options={STATUS_OPTIONS} formProps={formProps} defaultValue="active" />
              )}
            </div>

            <div className="ev-section">
              <h3>Dome</h3>
              <p className="ev-hint">
                This dome is created with the type and has no sub-domes. Posts of this type go into it automatically, and users cannot pick it for anything else.
              </p>
              <FieldTextInput id="domeName" name="domeName" formName="specialPost" className="textInput" type="text" formProps={formProps} label="*Dome name" disabled={saving} />
              <FieldTextInput id="domeDescription" name="domeDescription" formName="specialPost" className="textInput" type="textarea" rows={3} maxLength={250} formProps={formProps} label="Dome description" disabled={saving} />
              <div className="profileWrapperimg">
                <div className="selection_box">
                  <h4>*Dome icon (JPG or PNG, 50 MB max)</h4>
                  <FieldFileInput
                    id="icon"
                    name="icon"
                    className="textInput"
                    formProps={formProps}
                    rootClassName="input_upload"
                    enableDrop
                  />
                </div>
              </div>
            </div>

            {firstFormError && <ValidationError className="validation_error" error={firstFormError} />}
            <ErrorBox error={formError} />

            {saving ? (
              <div className="savingLoader"><CircularProgressLoader /></div>
            ) : (
              <div className="buttonWrapper">
                <Button buttonType="button" className="ev-btn-ghost" onClick={() => navigate('/specialposts')}>Cancel</Button>
                <AccessControl permissionKey={PERMISSION_KEYS.SPECIAL_POSTS} accessType={isEdit ? PERMISSION_TYPES.EDIT : PERMISSION_TYPES.ADD}>
                  <Button buttonType="submit">{isEdit ? 'Save' : 'Create'}</Button>
                </AccessControl>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ManageSpecialPost;
